import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  TextField,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  Send as SendIcon,
  Image as ImageIcon,
  FileCopy as FileCopyIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Conversation, Message } from './chatTypes';
import { formatTime, getInitials, getUserId } from './chatUtils';

interface ChatViewProps {
  conversation: Conversation | null;
  onSendMessage: (content: string, mediaUrl?: string) => void;
  isExpanded: boolean;
  loading: boolean;
  onBack: () => void;
}

const SkeletonMessage: React.FC<{ isRight?: boolean }> = ({ isRight = false }) => {
  return (
    <Box sx={{
      mb: 2,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: isRight ? 'flex-end' : 'flex-start'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        width: '100%',
        justifyContent: isRight ? 'flex-end' : 'flex-start',
      }}>
        {/* {!isRight && (
          <Skeleton variant="circular" width={32} height={32} />
        )} */}
        <Box sx={{
          maxWidth: '80%',
          minWidth: '120px',
          p: '8px 8px 3px 8px',
          borderRadius: '8px',
          bgcolor: isRight ? 'rgba(25, 118, 210, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
        }}>
          <Skeleton variant="text" width={150} height={13} />
          {/* <Skeleton variant="text" width={100} height={12} /> */}
          <Skeleton variant="text" width={60} height={10} sx={{ my: 0.5 }} />
        </Box>
      </Box>
    </Box>
  );
};

const MessageBubble: React.FC<{
  message: Message;
  isCurrentUser: boolean;
  showSender: boolean;
  senderName: string;
  isExpanded: boolean;
}> = ({ message, isCurrentUser, showSender, senderName, isExpanded }) => {

  const bubbleStyles = useMemo(() => ({
    maxWidth: isCurrentUser ? '80%' : '80%',
    minWidth: '120px',
    p: '8px 8px 3px 8px',
    borderRadius: 2,
    wordBreak: 'break-all',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
    opacity: 1,
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: isCurrentUser ? 'translateX(10px)' : 'translateX(-10px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
    '&:hover': {
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
    },
    ...(isCurrentUser
      ? {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderBottomRightRadius: 0,
      }
      : {
        bgcolor: 'background.paper',
        borderBottomLeftRadius: 0,
      }),
  }), [isCurrentUser, isExpanded]);

  return (
    <Box sx={{
      mb: 2,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: isCurrentUser ? 'flex-end' : 'flex-start'
    }}>
      {showSender && !isCurrentUser && (
        <Typography
          variant="caption"
          sx={{ ml: 6, mb: 0.5, color: 'text.secondary' }}
        >
          {senderName}
        </Typography>
      )}
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        width: '100%',
        justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
      }}>
        {!isCurrentUser && showSender && (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {getInitials(senderName)}
          </Avatar>
        )}
        <Box sx={bubbleStyles}>
          {message.mediaUrl && (
            <Box sx={{ mb: 1 }}>
              {message.mediaUrl.includes('image') ? (
                <Box
                  component="img"
                  src={message.mediaUrl}
                  alt="Shared media"
                  sx={{
                    maxHeight: 240,
                    maxWidth: '100%',
                    borderRadius: 1,
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isCurrentUser ? 'primary.dark' : 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isCurrentUser ? 'primary.darker' : 'action.selected',
                    },
                  }}
                >
                  <FileCopyIcon fontSize="small" />
                  <Typography variant="body2">Attached file</Typography>
                </Box>
              )}
            </Box>
          )}
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: isCurrentUser ? '#FFFFFF' : '#212121',
            }}
          >{message.content}</Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.125,
              color: isCurrentUser ? '#BFDBFE' : '#6B7280',
              fontSize: '10px',
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ChatView: React.FC<ChatViewProps> = ({
  conversation,
  onSendMessage,
  isExpanded,
  loading,
  onBack,
}) => {

  const styles = useMemo(() => ({
    root: {
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      px: 2,
      py: .5,
      borderBottom: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '53px',
      transition: 'height 0.3s ease',
      boxShadow: '0 10px 6px 1px #f5f5f5'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      bgcolor: '#f5f5f5',
      p: 1.5,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      scrollBehavior: 'smooth',
    },
    inputContainer: {
      p: 1,
      borderTop: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex',
      alignItems: 'flex-end',
      gap: 1,
      transition: 'all 0.3s ease',
    },
    messageInput: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        fontSize: "14px",
        color: "#3D4C5E",
        borderRadius: '8px',
        minHeight: "30px",
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&.Mui-focused': {
          boxShadow: '0 0 0 2px rgba(61, 139, 248, 0.2)',
        },
        '&:hover': {
          borderColor: 'primary.main',
        },
      },
    },
    mediaOptions: {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 3,
      p: 1,
      display: 'flex',
      gap: 1,
      animation: 'slideUp 0.2s ease-out',
      '@keyframes slideUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
    },
    mediaButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 1,
      borderRadius: 1,
      transition: 'background-color 0.2s ease, transform 0.2s ease',
      '&:hover': {
        bgcolor: 'action.hover',
        transform: 'translateY(-2px)',
      },
    },
    mediaButtonLabel: {
      fontSize: '0.75rem',
      mt: 0.5,
    },
    infoDrawer: {
      width: 320,
      '& .MuiDrawer-paper': {
        width: 320,
        position: 'relative',
        height: '100%',
        border: 'none',
        borderLeft: 1,
        borderColor: 'divider',
        transition: 'transform 0.3s ease',
      },
    },
    participantStatus: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: 2,
      borderColor: 'background.paper',
      transition: 'transform 0.2s ease',
    },
    backButton: {
      transition: 'transform 0.2s ease, background-color 0.2s ease',
      '&:hover': {
        transform: 'scale(1.1)',
        bgcolor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    sendButton: {
      transition: 'transform 0.2s ease, opacity 0.2s ease',
      '&:not(.Mui-disabled)': {
        '&:hover': {
          transform: 'scale(1.1)',
        },
      },
      '&.Mui-disabled': {
        opacity: 0.6,
      },
    },
    conversationInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      // cursor: 'pointer',
      // transition: 'transform 0.2s ease',
      // '&:hover': {
      //   transform: 'translateX(2px)',
      // },
    },
  }), [isExpanded]);

  const [newMessage, setNewMessage] = useState('');
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversation) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMediaUpload = (type: 'image' | 'file') => {
    const mockMediaUrl = type === 'image'
      ? 'https://via.placeholder.com/300'
      : 'https://example.com/file.pdf';

    onSendMessage(`Sent a ${type}`, mockMediaUrl);
    setShowMediaOptions(false);
  };

  return (
    <Box sx={styles.root}>
      {conversation === null ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 2,
        }}>
          <Typography variant="h6" gutterBottom>
            No conversation selected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a conversation from the sidebar.
          </Typography>
        </Box>
      ) : <>
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                color: 'inherit',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box
              sx={styles.conversationInfo}
              onClick={() => setShowGroupInfo(!showGroupInfo)}
            >
              {conversation && conversation?.isGroup ? (
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <GroupIcon />
                </Avatar>
              ) : (
                // <Badge
                //   overlap="circular"
                //   anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                //   variant="dot"
                //   color={
                //     conversation?.participants[0].status === 'online'
                //       ? 'success'
                //       : conversation?.participants[0].status === 'busy'
                //         ? 'error'
                //         : conversation?.participants[0].status === 'away'
                //           ? 'warning'
                //           : 'default'
                //   }
                // >
                <Avatar sx={{ width: 32, height: 32, fontSize: '14px', color: "#212121", fontWeight: "500", background: "#e5e7eb" }}>{getInitials(conversation?.title)}</Avatar>
                // </Badge>
              )}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '1px',
              }}>
                <Typography variant="subtitle2" fontSize={'15px'} lineHeight={'1.4'}>{conversation?.title}</Typography>
                {/* <Typography variant="caption" color="text.secondary" fontSize="10px" lineHeight={'1.2'}  >
                  {conversation?.isGroup
                    ? `${conversation?.participants.length} participants`
                    : conversation?.participants[0].status}
                </Typography> */}
              </Box>
            </Box>
          </Box>
          {/* <Box sx={styles.headerRight}>
              {conversation.isGroup && (
                <IconButton size="small">
                  <VideocamIcon />
                </IconButton>
              )}
              <IconButton size="small">
                <PhoneIcon />
              </IconButton>
              <IconButton size="small">
                <MoreVertIcon />
              </IconButton>
            </Box> */}
        </Box>

        <Box sx={styles.messagesContainer}>
          {loading ? (conversation?.messages.length === 0 ?
            <>
              <SkeletonMessage />
              <SkeletonMessage isRight={true} />
              <SkeletonMessage />
              <SkeletonMessage isRight={true} />
              <SkeletonMessage />
            </> : conversation?.messages.map((message) => (
              <SkeletonMessage key={message.id + 'skeleton'} isRight={message.senderId === getUserId()} />
            ))
          ) : (
            // Actual messages
            conversation?.messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isCurrentUser={message.senderId === getUserId()}
                showSender={
                  conversation?.isGroup &&
                  (index === 0 ||
                    conversation?.messages[index - 1].senderId !== message.senderId)
                }
                senderName={
                  conversation?.participants.find((p) => p?.id?.toString() === message.senderId)
                    ?.name || 'Unknown'
                }
                isExpanded={isExpanded}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={styles.inputContainer}>
          <Box sx={{ position: 'relative' }}>
            {/* <IconButton
              sx={{
                mb: '4px',
                transition: 'transform 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              size="small"
              disabled={loading}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton> */}
            {showMediaOptions && (
              <Box sx={styles.mediaOptions}>
                <Box
                  sx={styles.mediaButton}
                  onClick={() => handleMediaUpload('image')}
                >
                  <ImageIcon color="primary" fontSize="small" />
                  <Typography sx={styles.mediaButtonLabel}>Image</Typography>
                </Box>
                <Box
                  sx={styles.mediaButton}
                  onClick={() => handleMediaUpload('file')}
                >
                  <FileCopyIcon color="primary" fontSize="small" />
                  <Typography sx={styles.mediaButtonLabel}>File</Typography>
                </Box>
              </Box>
            )}
          </Box>
          <TextField
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            size="small"
            sx={styles.messageInput}
            disabled={loading}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loading}
            color="primary"
            size="small"
            sx={styles.sendButton}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </>
      }
    </Box>
  );
};

export default ChatView;